import { PhoneNumberUtil } from 'google-libphonenumber';
import logger from '@/utils/logger';
const phoneUtil = PhoneNumberUtil.getInstance();

export function isValidPhoneNumber(fullMobileNumber: string): boolean {
    try {
        const phoneNumber = phoneUtil.parseAndKeepRawInput(fullMobileNumber);
        return phoneUtil.isValidNumber(phoneNumber); // Returns true if valid
    } catch (error) {
        return false; // Invalid phone number
    }
}

export function isValidEmail(email: string): boolean {
    const trimmedEmail = email.trim().toLowerCase();

    // Check for consecutive hyphens, dots, dot-hyphen, hyphen-dot, or consecutive dots
    if (
        trimmedEmail.includes('--') ||
        trimmedEmail.includes('..') ||
        trimmedEmail.includes('.-') ||
        trimmedEmail.includes('-.')
    ) {
        return false;
    }

    // Check if email starts or ends with a dot or hyphen
    if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.') || trimmedEmail.startsWith('-') || trimmedEmail.endsWith('-')) {
        return false;
    }

    // Regex for email validation
    const emailRegex = /^[a-z0-9](?:[a-z0-9+._-]*[a-z0-9])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.[a-z]{2,63}$/;

    // Additional checks
    if (!emailRegex.test(trimmedEmail)) {
        return false;
    }

    // Ensure local part and domain are not empty and within length limits
    const [localPart, domain] = trimmedEmail.split('@');
    if (localPart.length > 64 || domain.length > 255) {
        return false;
    }

    return true;
}

export const isValidFullName = (name: string): boolean => {
    const fullNameRegex = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;
    return fullNameRegex.test(name.trim());
};

// Username validation (reusing the logic from previous Register.tsx)
export const isValidUsername = (username: string): boolean => {
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    return usernameRegex.test(username) && username.length >= 4;
};

// Password validation - checks for strong password requirements
export const isValidPassword = (password: string): boolean => {
    // Password must:
    // - Be at least 8 characters long
    // - Contain at least one uppercase letter
    // - Contain at least one number
    // - Contain at least one special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
};

/**
 * Formats a date into the standard display format: "25 Jun 2025"
 * @param date - Date object or ISO string or timestamp
 * @param fallbackText - Optional text to display if date is invalid (default: "-")
 * @returns Formatted date string
 */
export const formatDisplayDate = (date: Date | string | number | null | undefined, fallbackText: string = "-"): string => {
    if (!date) return fallbackText;

    try {
        const dateObj = date instanceof Date ? date : new Date(date);

        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return fallbackText;
        }

        // Format as "25 Jun 2025"
        const day = dateObj.getDate();
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const year = dateObj.getFullYear();

        return `${day} ${month} ${year}`;
    } catch (error) {
        logger.error("Error formatting date:", error);
        return fallbackText;
    }
};


export const normalizeDefault = (value: string | boolean | undefined): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "yes";
    return false;
};


export const normalizeUrl = (url: string): string => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }
    return `https://${url}`;
};